import { i18n, formatNumber } from './i18n'
import type { JsonObject } from './types'

export function uiText(de: string, en: string): string {
  return i18n.locale === 'de' ? de : en
}

export function asObject(value: unknown): JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as JsonObject)
    : {}
}

export function asList(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

export function asArray(value: unknown): unknown[] {
  return asList(value)
}

export function asObjectList(value: unknown): JsonObject[] {
  return asList(value).filter(
    (item): item is JsonObject =>
      item !== null && typeof item === 'object' && !Array.isArray(item),
  )
}

export function text(value: unknown, fallback = '—'): string {
  if (typeof value === 'string' && value.trim() !== '') return value
  if (typeof value === 'number' || typeof value === 'bigint')
    return String(value)
  return fallback
}

export function localized(value: unknown, fallback = ''): string {
  if (typeof value === 'string' && value.trim() !== '')
    return fallback !== '' ? fallback : value
  const object = asObject(value)
  const localeValue = object[i18n.locale]
  if (typeof localeValue === 'string' && localeValue.trim() !== '')
    return localeValue
  return fallback
}

export function humanize(value: string): string {
  return value
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function standardBuildingLabel(type: string): string {
  const key = type.trim().toLowerCase()
  const labels: Record<string, [string, string]> = {
    academy: ['Akademie', 'Academy'],
    barracks: ['Kaserne', 'Barracks'],
    clay_pit: ['Lehmgrube', 'Clay Pit'],
    farm: ['Bauernhof', 'Farm'],
    headquarters: ['Hauptgebäude', 'Headquarters'],
    iron_mine: ['Eisenmine', 'Iron Mine'],
    market: ['Markt', 'Market'],
    nobility: ['Adelshof', 'Nobility'],
    rally_point: ['Versammlungsplatz', 'Rally Point'],
    smithy: ['Schmiede', 'Smithy'],
    stable: ['Stall', 'Stable'],
    wall: ['Mauer', 'Wall'],
    warehouse: ['Speicher', 'Warehouse'],
    woodcutter: ['Holzfäller', 'Woodcutter'],
    workshop: ['Werkstatt', 'Workshop'],
  }
  const label = labels[key]
  return label
    ? uiText(label[0], label[1])
    : uiText('Unbekanntes Gebäude', 'Unknown building')
}

export function resourceLabel(type: string): string {
  const key = type.trim().toLowerCase()
  const labels: Record<string, [string, string]> = {
    wood: ['Holz', 'Wood'],
    clay: ['Lehm', 'Clay'],
    iron: ['Eisen', 'Iron'],
    grain: ['Getreide', 'Grain'],
    food: ['Getreide', 'Food'],
    stone: ['Stein', 'Stone'],
    silver: ['Silber', 'Silver'],
    gold: ['Gold', 'Gold'],
    population: ['Bevölkerung', 'Population'],
  }
  const label = labels[key]
  return label ? uiText(label[0], label[1]) : uiText('Rohstoff', 'Resource')
}

export function stateLabel(state: string): string {
  const key = state.trim().toUpperCase()
  const labels: Record<string, [string, string]> = {
    ACTIVE: ['Aktiv', 'Active'],
    DISABLED: ['Deaktiviert', 'Disabled'],
    DRAFT: ['Entwurf', 'Draft'],
    IN_REVIEW: ['In Prüfung', 'In review'],
    REVIEW: ['Prüfung', 'Review'],
    APPROVED: ['Genehmigt', 'Approved'],
    PUBLISHED: ['Veröffentlicht', 'Published'],
    ARCHIVED: ['Archiviert', 'Archived'],
    GENERATING: ['Wird erzeugt', 'Generating'],
    PREPARED: ['Vorbereitet', 'Prepared'],
    SCHEDULED: ['Geplant', 'Scheduled'],
    REGISTRATION_OPEN: ['Anmeldung offen', 'Registration open'],
    UPCOMING: ['Demnächst', 'Upcoming'],
    RUNNING: ['Läuft', 'Running'],
    PAUSED: ['Pausiert', 'Paused'],
    MAINTENANCE: ['Wartung', 'Maintenance'],
    ENDGAME: ['Endphase', 'Endgame'],
    CONCLUDED: ['Beendet', 'Concluded'],
    FINISHED: ['Beendet', 'Finished'],
    FAILED: ['Fehlgeschlagen', 'Failed'],
    ABORTED: ['Abgebrochen', 'Aborted'],
    COMPLETED: ['Abgeschlossen', 'Completed'],
    OUTBOUND: ['Unterwegs', 'Outbound'],
    RETURNING: ['Auf Rückweg', 'Returning'],
    RECALLED: ['Zurückgerufen', 'Recalled'],
    ARRIVED: ['Angekommen', 'Arrived'],
    RESOLVING: ['Wird aufgelöst', 'Resolving'],
    RESOLVED: ['Erledigt', 'Resolved'],
    INCOMING: ['Eingehend', 'Incoming'],
    ACCEPTED: ['Akzeptiert', 'Accepted'],
    ONLINE: ['Online', 'Online'],
    READY: ['Bereit', 'Ready'],
    WAITING: ['Wartend', 'Waiting'],
    QUEUED: ['Warteschlange', 'Queued'],
    PENDING: ['Ausstehend', 'Pending'],
    REJECTED: ['Abgelehnt', 'Rejected'],
    SUSPENDED: ['Gesperrt', 'Suspended'],
    CLAIMED: ['Abgeholt', 'Claimed'],
    REWARD_AVAILABLE: ['Belohnung verfügbar', 'Reward available'],
    EXPIRED: ['Abgelaufen', 'Expired'],
    OPEN: ['Offen', 'Open'],
    CLOSED: ['Geschlossen', 'Closed'],
    PRODUCTION: ['Produktion', 'Production'],
    TEST: ['Test', 'Test'],
    STAGING: ['Staging', 'Staging'],
    PLAYER: ['Spieler', 'Player'],
    BARBARIAN: ['Barbar', 'Barbarian'],
    NPC: ['Neutral', 'Neutral'],
  }
  const label = labels[key]
  return label ? uiText(label[0], label[1]) : uiText('Status', 'Status')
}

export function publicWorldName(
  displayName: string | null | undefined,
  worldId: string,
): string {
  const raw = displayName?.trim() ?? ''
  if (raw !== '' && raw.length <= 128) return raw
  const compact = worldId.replaceAll('-', '').slice(0, 8).toUpperCase()
  return uiText(`Welt ${compact}`, `World ${compact}`)
}

export function micro(value: unknown, digits = 0): string {
  const number = Number(value)
  if (!Number.isFinite(number)) return '—'
  return formatNumber(number / 1_000_000, digits)
}

export function numberValue(value: unknown): number {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

export function boolValue(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true'
}

export function worldDuration(us: unknown): string {
  const raw = Number(us)
  if (!Number.isFinite(raw) || raw < 0) return '—'
  const total = Math.floor(raw / 1_000_000)
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  if (days > 0)
    return uiText(
      `${String(days)} T ${String(hours)} Std ${String(minutes)} Min`,
      `${String(days)}d ${String(hours)}h ${String(minutes)}m`,
    )
  if (hours > 0)
    return uiText(
      `${String(hours)} Std ${String(minutes)} Min ${String(seconds)} Sek`,
      `${String(hours)}h ${String(minutes)}m ${String(seconds)}s`,
    )
  if (minutes > 0)
    return uiText(
      `${String(minutes)} Min ${String(seconds)} Sek`,
      `${String(minutes)}m ${String(seconds)}s`,
    )
  return uiText(`${String(seconds)} Sek`, `${String(seconds)}s`)
}

export function formatWorldDuration(us: unknown): string {
  return worldDuration(us)
}

export function objectEntries(value: unknown): Array<[string, unknown]> {
  return Object.entries(asObject(value))
}

export function directionLabel(direction: string): string {
  const key = direction.trim().toUpperCase()
  const labels: Record<string, [string, string]> = {
    INCOMING: ['Eingehend', 'Incoming'],
    OUTGOING: ['Ausgehend', 'Outgoing'],
    OWN: ['Ausgehend', 'Outgoing'],
    ACCEPTED: ['Angenommen', 'Accepted'],
    REJECTED: ['Abgelehnt', 'Rejected'],
    PENDING: ['Offen', 'Pending'],
    PERSONAL: ['Persönlich', 'Personal'],
    ALLIANCE: ['Allianz', 'Alliance'],
    VILLAGE: ['Dorf', 'Village'],
  }
  const label = labels[key]
  return label ? uiText(label[0], label[1]) : stateLabel(direction)
}

export function effectLabel(key: string): string {
  const normalized = key
    .replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`)
    .toLowerCase()
  const labels: Record<string, [string, string]> = {
    build_speed_multiplier_bps: ['Baugeschwindigkeit', 'Build speed'],
    building_speed_multiplier_bps: ['Baugeschwindigkeit', 'Build speed'],
    training_speed_multiplier_bps: [
      'Ausbildungsgeschwindigkeit',
      'Training speed',
    ],
    research_speed_multiplier_bps: [
      'Forschungsgeschwindigkeit',
      'Research speed',
    ],
    smithy_speed_multiplier_bps: ['Schmiedegeschwindigkeit', 'Smithy speed'],
    population_usage: ['Bevölkerungsbedarf', 'Population usage'],
    population_supply: ['Bevölkerungskapazität', 'Population capacity'],
    storage_capacity_delta_micro: ['Lagerkapazität', 'Storage capacity'],
    merchant_capacity_delta_micro: ['Händlerkapazität', 'Merchant capacity'],
    merchant_count_delta: ['Händler', 'Merchants'],
    resource_production_rate_delta_micro_per_second: [
      'Produktion',
      'Production',
    ],
    wall_defense_multiplier_bps: ['Verteidigungsbonus', 'Defense bonus'],
    defense_multiplier_bps: ['Verteidigungsbonus', 'Defense bonus'],
    points: ['Punkte', 'Points'],
  }
  const label = labels[normalized]
  return label ? uiText(label[0], label[1]) : uiText('Effekt', 'Effect')
}

export function effectValue(key: string, value: unknown): string {
  const normalized = key
    .replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`)
    .toLowerCase()
  if (normalized.endsWith('_bps')) {
    const number = Number(value)
    if (!Number.isFinite(number)) return text(value)
    const delta = number >= 10000 ? number - 10000 : number
    const percent = delta / 100
    return `${percent > 0 ? '+' : ''}${formatNumber(percent, Math.abs(percent) < 10 ? 1 : 0)} %`
  }
  if (normalized.includes('_micro_per_second')) {
    const number = Number(value)
    if (!Number.isFinite(number)) return text(value)
    return `${formatNumber((number / 1_000_000) * 3600, 0)} / h`
  }
  if (normalized.includes('_micro')) return micro(value)
  if (typeof value === 'boolean')
    return value ? uiText('Ja', 'Yes') : uiText('Nein', 'No')
  if (typeof value === 'number') return formatNumber(value)
  return text(value)
}

export function reportFieldLabel(key: string): string {
  const normalized = key
    .replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`)
    .toLowerCase()
  const labels: Record<string, [string, string]> = {
    report_id: ['Bericht-ID', 'Report ID'],
    kind: ['Art', 'Type'],
    created_at: ['Zeitpunkt', 'Created'],
    attacker: ['Angreifer', 'Attacker'],
    defender: ['Verteidiger', 'Defender'],
    origin: ['Ausgangsort', 'Origin'],
    target: ['Ziel', 'Target'],
    result: ['Ergebnis', 'Result'],
    outcome: ['Ergebnis', 'Outcome'],
    resources: ['Rohstoffe', 'Resources'],
    loot: ['Beute', 'Loot'],
    units: ['Einheiten', 'Units'],
    losses: ['Verluste', 'Losses'],
    wall: ['Mauer', 'Wall'],
    loyalty: ['Loyalität', 'Loyalty'],
    distance: ['Entfernung', 'Distance'],
  }
  const label = labels[normalized]
  return label ? uiText(label[0], label[1]) : humanize(key)
}

export function compactValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean')
    return value ? uiText('Ja', 'Yes') : uiText('Nein', 'No')
  if (typeof value === 'number') return formatNumber(value)
  if (typeof value === 'string') return value
  if (Array.isArray(value))
    return value.map((item) => compactValue(item)).join(', ')
  const object = asObject(value)
  const entries = Object.entries(object)
  if (entries.length === 0) return '—'
  return entries
    .map(([key, item]) => `${humanize(key)}: ${compactValue(item)}`)
    .join(' · ')
}

export function stateTone(
  state: string,
): 'good' | 'warn' | 'danger' | 'neutral' {
  if (/ACTIVE|COMPLETED|ACCEPTED|ONLINE|READY|PUBLISHED/.test(state))
    return 'good'
  if (/WAIT|QUEUED|PENDING|PAUSED|ENDGAME|REVIEW|SCHEDULED/.test(state))
    return 'warn'
  if (/FAILED|CANCEL|REJECT|SUSPEND|ABORT|SECURITY/.test(state)) return 'danger'
  return 'neutral'
}

export function formatDateTime(value: unknown): string {
  if (typeof value !== 'string' || value === '') return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(i18n.locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date)
}

export function formatPreciseDateTime(value: unknown): string {
  if (typeof value !== 'string' || value === '') return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(i18n.locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
      }).format(date)
}

export function problemText(error: unknown): string {
  if (error instanceof Error && error.message !== '') return error.message
  return uiText('Die Aktion ist fehlgeschlagen.', 'The action failed.')
}

export function queryString(
  params: Record<string, string | number | undefined>,
): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && String(value) !== '')
      query.set(key, String(value))
  }
  const output = query.toString()
  return output === '' ? '' : `?${output}`
}
